import React, { useCallback, useMemo, useState } from "react";
import { useROStore } from "../context/ROContext";
import { useAuth } from "../context/AuthContext";
import { useROUnread } from "../context/ROUnreadContext";
import ROActivityDrawer from "../components/ro/ROActivityDrawer";
import {
  PARTS_ACTION_STATUS,
  PARTS_REQUEST_TYPE,
  PARTS_STATUS,
  RO_LIFECYCLE,
  RO_STATUS,
} from "../constants/roStatus";
import {
  inDateRangeISO,
  normalizeROArray,
  toISODateKey,
} from "../utils/metricsEngine";

const DATE_FILTERS = Object.freeze({
  TODAY: "TODAY",
  YESTERDAY: "YESTERDAY",
  THIS_WEEK: "THIS_WEEK",
  THIS_MONTH: "THIS_MONTH",
  CUSTOM: "CUSTOM",
});

function safeString(v) {
  return typeof v === "string" ? v : v == null ? "" : String(v);
}

function safeNumber(v, fallback = 0) {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
}

function statusKey(v) {
  return safeString(v).trim().toUpperCase().replace(/[\s-]+/g, "_");
}

function toTime(value) {
  const raw = safeString(value).trim();
  if (!raw) return 0;
  const t = new Date(raw).getTime();
  return Number.isFinite(t) ? t : 0;
}

function formatDurationFromMs(ms) {
  if (!Number.isFinite(ms) || ms < 0) return "—";

  const mins = Math.max(0, Math.round(ms / 60000));
  if (mins < 60) return `${mins}m`;

  const hours = Math.floor(mins / 60);
  const rem = mins % 60;
  if (hours < 24) return rem ? `${hours}h ${rem}m` : `${hours}h`;

  const days = Math.floor(hours / 24);
  const rh = hours % 24;
  return rh ? `${days}d ${rh}h` : `${days}d`;
}

function ageFromISO(iso) {
  const t = toTime(iso);
  if (!t) return null;
  return Math.max(0, Date.now() - t);
}

function compactDateTime(value) {
  const t = toTime(value);
  if (!t) return "—";

  return new Date(t).toLocaleString([], {
    month: "numeric",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function formatPromise(value) {
  const t = toTime(value);
  if (!t) return "No promise";

  const diff = t - Date.now();
  const d = new Date(t);
  const timeText = d.toLocaleTimeString([], {
    hour: "numeric",
    minute: "2-digit",
  });

  if (diff < 0) return `Overdue ${formatDurationFromMs(Math.abs(diff))}`;
  if (diff <= 60 * 60000) return `Due in ${formatDurationFromMs(diff)}`;

  const today = new Date();
  if (
    d.getFullYear() === today.getFullYear() &&
    d.getMonth() === today.getMonth() &&
    d.getDate() === today.getDate()
  ) {
    return `Today ${timeText}`;
  }

  return d.toLocaleString([], {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function addDays(input, days) {
  const d = new Date(input);
  d.setDate(d.getDate() + days);
  return d;
}

function startOfLocalWeekMonday(input = new Date()) {
  const d = new Date(input);
  d.setHours(0, 0, 0, 0);
  const day = d.getDay();
  return addDays(d, day === 0 ? -6 : 1 - day);
}

function presetDateRange(filter) {
  const today = new Date();
  const todayKey = toISODateKey(today);

  if (filter === DATE_FILTERS.YESTERDAY) {
    const key = toISODateKey(addDays(today, -1));
    return { from: key, to: key, label: "Yesterday" };
  }

  if (filter === DATE_FILTERS.THIS_WEEK) {
    return {
      from: toISODateKey(startOfLocalWeekMonday(today)),
      to: todayKey,
      label: "This Week",
    };
  }

  if (filter === DATE_FILTERS.THIS_MONTH) {
    return {
      from: toISODateKey(new Date(today.getFullYear(), today.getMonth(), 1)),
      to: todayKey,
      label: "This Month",
    };
  }

  return { from: todayKey, to: todayKey, label: "Today" };
}

function getHistoryEvents(ro) {
  return Array.isArray(ro?.historyEvents) ? ro.historyEvents : [];
}

function eventType(event) {
  return statusKey(event?.type || event?.eventType);
}

function eventISO(event) {
  return safeString(
    event?.at || event?.ts || event?.timestamp || event?.createdAt || event?.created_at || ""
  ).trim();
}

function latestEvent(ro, typeNames) {
  const wanted = new Set((typeNames || []).map(statusKey));

  return getHistoryEvents(ro)
    .filter((event) => wanted.has(eventType(event)))
    .sort((a, b) => toTime(eventISO(b)) - toTime(eventISO(a)))[0] || null;
}

function getPartsRequestISO(ro) {
  const direct = safeString(
    ro?.timestamps?.partsRequestedAt ||
      ro?.partsRequestedAt ||
      ro?.partsRequestedAtISO ||
      ro?.parts_requested_at ||
      ""
  ).trim();

  if (direct) return direct;

  return eventISO(latestEvent(ro, ["PARTS_REQUESTED"]));
}

function getPartsActionISO(ro) {
  const direct = safeString(
    ro?.partsActionedAtISO ||
      ro?.partsActionedAt ||
      ro?.partsLastActionAtISO ||
      ro?.partsStatusUpdatedAt ||
      ro?.timestamps?.partsActionedAt ||
      ro?.timestamps?.partsStatusUpdatedAt ||
      ""
  ).trim();

  if (direct) return direct;

  return eventISO(
    latestEvent(ro, [
      "PARTS_ACTION_UPDATED",
      "PARTS_STATUS_UPDATED",
      "PARTS_ETA_UPDATED",
    ])
  );
}

function getCreatedISO(ro) {
  return safeString(
    ro?.timestamps?.createdAt || ro?.createdAt || ro?.openedAt || ""
  ).trim();
}

function getPromiseISO(ro) {
  return safeString(
    ro?.promiseTimeISO || ro?.promiseTime || ro?.promise_time_iso || ""
  ).trim();
}

function isArchived(ro, store) {
  if (typeof store?.isArchived === "function") return !!store.isArchived(ro);

  const status = statusKey(ro?.status);
  const lifecycle = statusKey(ro?.lifecycle || ro?.lifeCycle);

  return (
    status === statusKey(RO_STATUS.ARCHIVED) ||
    lifecycle === "ARCHIVED" ||
    status === "VOID" ||
    status === "VOIDED"
  );
}

function isCompleted(ro, store) {
  if (typeof store?.isCompletedRO === "function") return !!store.isCompletedRO(ro);

  const status = statusKey(ro?.status);
  const lifecycle = statusKey(ro?.lifecycle || ro?.lifeCycle);

  return (
    status === statusKey(RO_STATUS.COMPLETED) ||
    lifecycle === statusKey(RO_LIFECYCLE.COMPLETED)
  );
}

function isWipRO(ro, store) {
  if (typeof store?.isWipRO === "function") return !!store.isWipRO(ro);
  return !isArchived(ro, store) && !isCompleted(ro, store);
}

function isVehicleGone(ro) {
  const pickup = statusKey(
    ro?.customerPickupStatus || ro?.customer_pickup_status || ""
  );
  const advisor = statusKey(
    ro?.advisorActionStatus || ro?.advisor_action_status || ""
  );

  return (
    pickup === "VEHICLE_GONE" ||
    advisor === "VEHICLE_GONE" ||
    !!ro?.vehicleGone ||
    !!ro?.vehicle_gone ||
    !!ro?.pickedUp ||
    !!ro?.picked_up ||
    !!ro?.pickedUpAt ||
    !!ro?.vehicleGoneAtISO
  );
}

function getActiveWip(all, store) {
  const source = Array.isArray(store?.wipROs) ? store.wipROs : all;
  return (Array.isArray(source) ? source : []).filter(
    (ro) => isWipRO(ro, store) && !isVehicleGone(ro)
  );
}

function getPartsStatus(ro) {
  return statusKey(ro?.partsStatus || ro?.parts_status || PARTS_STATUS.NONE);
}

function getPartsActionStatus(ro) {
  return statusKey(
    ro?.partsActionStatus || ro?.parts_action_status || PARTS_ACTION_STATUS.REVIEW
  );
}

function getPartsRequestType(ro) {
  const direct = statusKey(
    ro?.partsRequestType || ro?.parts_request_type || ""
  );
  if (direct) return direct;

  const requestEvent = latestEvent(ro, ["PARTS_REQUESTED"]);
  return statusKey(
    requestEvent?.meta?.partsRequestType ||
      requestEvent?.meta?.requestType ||
      ""
  );
}

function isReviewAction(actionStatus) {
  const s = statusKey(actionStatus);
  return !s || s === "REVIEW" || s === "OPEN" || s === "REOPEN";
}

function isActionedPartsRO(ro) {
  const ps = getPartsStatus(ro);
  const action = getPartsActionStatus(ro);

  const statusActioned = ["PULLED", "ORDERED", "ARRIVED", "QUOTED"].includes(ps);
  const actionActioned = !!action && !isReviewAction(action);

  return statusActioned || actionActioned;
}

function hasOpenPartsReview(ro) {
  const requestType = getPartsRequestType(ro);
  const hasRequest = [
    statusKey(PARTS_REQUEST_TYPE.NEED_PARTS),
    statusKey(PARTS_REQUEST_TYPE.NEED_PRICE),
  ].includes(requestType);

  return hasRequest && !isActionedPartsRO(ro) && isReviewAction(getPartsActionStatus(ro));
}

function isOrdered(ro) {
  return getPartsStatus(ro) === "ORDERED" || getPartsActionStatus(ro) === "ORDERED";
}

function isArrived(ro) {
  return getPartsStatus(ro) === "ARRIVED" || getPartsActionStatus(ro) === "ARRIVED";
}

function isQuoted(ro) {
  return getPartsStatus(ro) === "QUOTED" || getPartsActionStatus(ro) === "QUOTED";
}

function isPulled(ro) {
  return (
    getPartsStatus(ro) === "PULLED" ||
    ["PRE_PULLED", "PREPULLED"].includes(getPartsActionStatus(ro))
  );
}

function isInProgress(ro) {
  const status = statusKey(ro?.status);
  const lifecycle = statusKey(ro?.lifecycle || ro?.lifeCycle);
  return status === "IN_PROGRESS" || lifecycle === "IN_PROGRESS";
}

function isPartsRelevant(ro) {
  const lifecycle = statusKey(ro?.lifecycle || ro?.lifeCycle);

  return (
    hasOpenPartsReview(ro) ||
    isOrdered(ro) ||
    isArrived(ro) ||
    isQuoted(ro) ||
    isPulled(ro) ||
    lifecycle === "WAITING_FOR_PARTS" ||
    lifecycle === "WAITING_ON_PARTS"
  );
}

function isPartsBlocking(ro) {
  const lifecycle = statusKey(ro?.lifecycle || ro?.lifeCycle);
  return (
    hasOpenPartsReview(ro) ||
    isOrdered(ro) ||
    lifecycle === "WAITING_FOR_PARTS" ||
    lifecycle === "WAITING_ON_PARTS"
  );
}

function isPromiseRisk(ro) {
  if (!isPartsBlocking(ro)) return false;
  const promise = toTime(getPromiseISO(ro));
  if (!promise) return false;
  return promise - Date.now() <= 60 * 60000;
}

function reviewAgeMs(ro) {
  const anchor = getPartsRequestISO(ro) || getCreatedISO(ro);
  return ageFromISO(anchor);
}

function stageAgeMs(ro) {
  const anchor = getPartsActionISO(ro) || getPartsRequestISO(ro) || getCreatedISO(ro);
  return ageFromISO(anchor);
}

function partsStage(ro) {
  if (hasOpenPartsReview(ro)) return "Needs Review";
  if (isOrdered(ro)) return "Ordered";
  if (isArrived(ro)) return "Arrived";
  if (isQuoted(ro)) return "Quoted";
  if (isPulled(ro)) return "Pre-Pulled";

  const action = getPartsActionStatus(ro);
  if (action === "NO_ACTION") return "No Action";

  return "Parts Follow-Up";
}

function requestTypeLabel(ro) {
  const type = getPartsRequestType(ro);
  if (type === "NEED_PRICE") return "Quote Requested";
  if (type === "NEED_PARTS") return "Parts Needed";
  return "";
}

function noteTextFromValue(value) {
  if (Array.isArray(value)) {
    return value
      .map((n) => {
        if (typeof n === "string") return n;
        if (n && typeof n === "object") {
          return safeString(n.text || n.body || n.note || n.message || "").trim();
        }
        return "";
      })
      .filter(Boolean)
      .join("\n\n");
  }

  if (value && typeof value === "object") {
    return safeString(
      value.text || value.body || value.note || value.message || ""
    ).trim();
  }

  const raw = safeString(value).trim();
  return raw === "[object Object]" ? "" : raw;
}

function notePreview(notes) {
  const raw = noteTextFromValue(notes);
  if (!raw) return "";

  const blocks = raw
    .split(/\n\s*\n/)
    .map((part) => part.trim())
    .filter(Boolean);

  if (!blocks.length) return "";

  const lines = blocks[blocks.length - 1]
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean);

  if (!lines.length) return "";
  return lines.length === 1 ? lines[0] : lines.slice(1).join(" ");
}

function findROById(list, id) {
  const clean = safeString(id).trim();
  if (!clean) return null;
  return (Array.isArray(list) ? list : []).find(
    (ro) => safeString(ro?.id).trim() === clean
  ) || null;
}

function getPartsRequestEvents(ro) {
  const history = getHistoryEvents(ro)
    .filter((event) => eventType(event) === "PARTS_REQUESTED")
    .map((event) => ({
      at: eventISO(event),
      type: statusKey(
        event?.meta?.partsRequestType || event?.meta?.requestType || ""
      ),
      actorRole: statusKey(
        event?.meta?.byRole || event?.meta?.actorRole || event?.byRole || ""
      ),
      actorName: safeString(
        event?.meta?.byName || event?.meta?.actorName || event?.byName || ""
      ).trim(),
    }))
    .filter((event) => event.at);

  if (history.length) return history;

  const direct = getPartsRequestISO(ro);
  return direct
    ? [
        {
          at: direct,
          type: getPartsRequestType(ro),
          actorRole: "",
          actorName: "",
        },
      ]
    : [];
}

function getPartsActionEvents(ro) {
  const actionTypes = new Set([
    "PARTS_ACTION_UPDATED",
    "PARTS_STATUS_UPDATED",
    "PARTS_ETA_UPDATED",
  ]);

  const history = getHistoryEvents(ro)
    .filter((event) => actionTypes.has(eventType(event)))
    .map((event) => ({
      at: eventISO(event),
      partsStatus: statusKey(event?.meta?.partsStatus || ""),
      actionStatus: statusKey(event?.meta?.partsActionStatus || ""),
    }))
    .filter((event) => event.at);

  if (history.length) return history;

  const direct = getPartsActionISO(ro);
  return direct
    ? [
        {
          at: direct,
          partsStatus: getPartsStatus(ro),
          actionStatus: getPartsActionStatus(ro),
        },
      ]
    : [];
}

function stageFromActionEvent(event, ro) {
  const ps = statusKey(event?.partsStatus);
  const action = statusKey(event?.actionStatus);

  if (ps === "ARRIVED" || action === "ARRIVED") return "Arrived";
  if (ps === "ORDERED" || action === "ORDERED") return "Ordered";
  if (ps === "QUOTED" || action === "QUOTED") return "Quoted";
  if (ps === "PULLED" || ["PRE_PULLED", "PREPULLED"].includes(action)) {
    return "Pre-Pulled";
  }
  if (action === "NO_ACTION") return "No Action";

  return partsStage(ro);
}

function topCounts(rows, keyGetter, limit = 5) {
  const counts = new Map();

  rows.forEach((row) => {
    const key = safeString(keyGetter(row)).trim();
    if (!key) return;
    counts.set(key, (counts.get(key) || 0) + 1);
  });

  return Array.from(counts.entries())
    .map(([name, count]) => ({ name, count }))
    .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name))
    .slice(0, limit);
}

function roNumberText(ro) {
  const roNumber = safeString(ro?.roNumber).trim();
  return roNumber ? `RO #${roNumber}` : "RO —";
}

function getTag(ro) {
  return safeString(
    ro?.tagNumber || ro?.tag_number || ro?.tag || ro?.keyTag || ""
  ).trim();
}

function attentionReasons(ro) {
  const reasons = [];

  if (isPromiseRisk(ro)) reasons.push("Promise Risk");
  if (isArrived(ro) && !isInProgress(ro)) reasons.push("Arrived · Work Not Started");
  if (hasOpenPartsReview(ro)) reasons.push(requestTypeLabel(ro) || "Needs Review");
  if (isOrdered(ro)) reasons.push("Ordered · Not Arrived");

  return reasons;
}

function attentionPriority(ro) {
  let score = 0;
  const promise = toTime(getPromiseISO(ro));

  if (isPromiseRisk(ro)) {
    score += 1000000;
    if (promise) score += Math.max(0, Date.now() - promise) / 1000;
  }

  if (isArrived(ro) && !isInProgress(ro)) score += 500000;

  const reviewAge = reviewAgeMs(ro);
  if (hasOpenPartsReview(ro)) {
    score += 250000 + safeNumber(reviewAge, 0) / 1000;
  }

  const stageAge = stageAgeMs(ro);
  if (isOrdered(ro)) score += 100000 + safeNumber(stageAge, 0) / 1000;

  return score;
}

function searchMatches(ro, query) {
  const q = safeString(query).trim().toLowerCase();
  if (!q) return true;

  return [
    ro?.roNumber,
    getTag(ro),
    ro?.customerName,
    ro?.vehicle,
    ro?.advisorName,
    ro?.assignedTechName,
    getPartsRequestType(ro),
    getPartsStatus(ro),
    getPartsActionStatus(ro),
    notePreview(ro?.notes),
  ].some((value) => safeString(value).toLowerCase().includes(q));
}

function tonePalette(tone) {
  if (tone === "danger") {
    return {
      border: "rgba(180,35,24,0.16)",
      bg: "linear-gradient(180deg, rgba(254,243,242,0.94), #fff)",
      value: "#b42318",
    };
  }

  if (tone === "warning") {
    return {
      border: "rgba(181,71,8,0.15)",
      bg: "linear-gradient(180deg, rgba(255,247,237,0.94), #fff)",
      value: "#b54708",
    };
  }

  if (tone === "good") {
    return {
      border: "rgba(6,118,71,0.14)",
      bg: "linear-gradient(180deg, rgba(236,253,243,0.88), #fff)",
      value: "#067647",
    };
  }

  if (tone === "parts") {
    return {
      border: "rgba(37,99,235,0.14)",
      bg: "linear-gradient(180deg, rgba(239,246,255,0.92), #fff)",
      value: "#1d4ed8",
    };
  }

  return {
    border: "rgba(15,23,42,0.08)",
    bg: "linear-gradient(180deg, #fff, #fbfcfd)",
    value: "#0f172a",
  };
}

function MetricCard({ label, value, sub, tone = "neutral" }) {
  const palette = tonePalette(tone);

  return (
    <div
      style={{
        border: `1px solid ${palette.border}`,
        background: palette.bg,
        borderRadius: 14,
        padding: 11,
        boxShadow: "0 7px 20px rgba(15,23,42,0.035)",
        minWidth: 0,
      }}
    >
      <div
        style={{
          fontSize: 10.5,
          fontWeight: 900,
          letterSpacing: "0.10em",
          textTransform: "uppercase",
          color: "rgba(15,23,42,0.60)",
        }}
      >
        {label}
      </div>
      <div
        style={{
          marginTop: 7,
          fontSize: 20,
          lineHeight: 1,
          fontWeight: 950,
          color: palette.value,
        }}
      >
        {value}
      </div>
      <div
        style={{
          marginTop: 6,
          fontSize: 11.2,
          lineHeight: 1.35,
          color: "rgba(15,23,42,0.58)",
        }}
      >
        {sub}
      </div>
    </div>
  );
}

function Section({ title, eyebrow, right, children, style }) {
  return (
    <section
      style={{
        border: "1px solid rgba(15,23,42,0.08)",
        background: "#ffffff",
        borderRadius: 14,
        padding: 11,
        boxShadow: "0 8px 24px rgba(15,23,42,0.035)",
        minWidth: 0,
        ...style,
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 10,
          marginBottom: 10,
        }}
      >
        <div style={{ minWidth: 0 }}>
          {eyebrow ? (
            <div
              style={{
                fontSize: 10.2,
                fontWeight: 900,
                letterSpacing: "0.11em",
                textTransform: "uppercase",
                color: "rgba(15,23,42,0.52)",
              }}
            >
              {eyebrow}
            </div>
          ) : null}
          <h2
            style={{
              margin: eyebrow ? "4px 0 0" : 0,
              fontSize: 15.5,
              lineHeight: 1.15,
              color: "#0f172a",
            }}
          >
            {title}
          </h2>
        </div>
        {right || null}
      </div>
      {children}
    </section>
  );
}

function Empty({ children }) {
  return (
    <div
      style={{
        padding: "18px 12px",
        border: "1px dashed rgba(15,23,42,0.12)",
        borderRadius: 12,
        background: "#fafbfc",
        textAlign: "center",
        color: "rgba(15,23,42,0.52)",
        fontSize: 12,
      }}
    >
      {children}
    </div>
  );
}

function Badge({ children, tone = "neutral" }) {
  const tones = {
    danger: { bg: "#fef2f2", border: "#fecaca", color: "#991b1b" },
    warning: { bg: "#fff7ed", border: "#fed7aa", color: "#9a3412" },
    parts: { bg: "#eff6ff", border: "#bfdbfe", color: "#1d4ed8" },
    good: { bg: "#ecfdf3", border: "#a7f3d0", color: "#067647" },
    neutral: { bg: "#f8fafc", border: "#e2e8f0", color: "#334155" },
  };
  const t = tones[tone] || tones.neutral;

  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        minHeight: 23,
        padding: "2px 7px",
        borderRadius: 999,
        border: `1px solid ${t.border}`,
        background: t.bg,
        color: t.color,
        fontSize: 10.2,
        fontWeight: 900,
        lineHeight: 1.1,
        whiteSpace: "nowrap",
      }}
    >
      {children}
    </span>
  );
}

function reasonTone(reason) {
  if (reason === "Promise Risk") return "danger";
  if (reason.startsWith("Arrived")) return "good";
  if (reason === "Quote Requested") return "warning";
  if (reason === "Parts Needed") return "parts";
  if (reason.startsWith("Ordered")) return "warning";
  return "neutral";
}

function AttentionRow({ ro, onOpenThread, hasUnreadNote }) {
  const reasons = attentionReasons(ro);
  const reviewAge = hasOpenPartsReview(ro) ? reviewAgeMs(ro) : null;
  const stageAge = !hasOpenPartsReview(ro) ? stageAgeMs(ro) : null;
  const promise = getPromiseISO(ro);
  const preview = notePreview(ro?.notes);

  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "minmax(180px, 1.2fr) minmax(220px, 1.5fr) minmax(150px, 0.9fr) auto",
        gap: 10,
        alignItems: "center",
        padding: "9px 0",
        borderTop: "1px solid rgba(15,23,42,0.07)",
      }}
    >
      <div style={{ minWidth: 0 }}>
        <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
          <strong style={{ fontSize: 12.2, color: "#0f172a" }}>
            {roNumberText(ro)}
          </strong>
          {getTag(ro) ? <Badge>Tag {getTag(ro)}</Badge> : null}
        </div>
        <div
          style={{
            marginTop: 4,
            fontSize: 11.5,
            color: "rgba(15,23,42,0.68)",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
          title={[ro?.customerName, ro?.vehicle].filter(Boolean).join(" · ")}
        >
          {[safeString(ro?.customerName).trim(), safeString(ro?.vehicle).trim()]
            .filter(Boolean)
            .join(" · ") || "No customer / vehicle"}
        </div>
      </div>

      <div style={{ minWidth: 0 }}>
        <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
          {reasons.map((reason) => (
            <Badge key={reason} tone={reasonTone(reason)}>
              {reason}
            </Badge>
          ))}
        </div>
        {preview ? (
          <div
            style={{
              marginTop: 5,
              fontSize: 10.8,
              color: "rgba(15,23,42,0.56)",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
            title={preview}
          >
            Latest note: {preview}
          </div>
        ) : null}
      </div>

      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 10.7, color: "rgba(15,23,42,0.58)" }}>
          Advisor: <b style={{ color: "#334155" }}>{safeString(ro?.advisorName).trim() || "—"}</b>
        </div>
        <div style={{ marginTop: 3, fontSize: 10.7, color: "rgba(15,23,42,0.58)" }}>
          Tech: <b style={{ color: "#334155" }}>{safeString(ro?.assignedTechName).trim() || "—"}</b>
        </div>
        <div
          style={{
            marginTop: 3,
            fontSize: 10.7,
            fontWeight: 900,
            color: isPromiseRisk(ro) ? "#b42318" : "rgba(15,23,42,0.58)",
          }}
        >
          {promise ? formatPromise(promise) : "No promise time"}
        </div>
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: 8, justifyContent: "flex-end" }}>
        <div style={{ textAlign: "right", minWidth: 58 }}>
          <div style={{ fontSize: 9.8, textTransform: "uppercase", letterSpacing: "0.08em", color: "rgba(15,23,42,0.45)", fontWeight: 900 }}>
            Age
          </div>
          <strong style={{ fontSize: 11.5, color: "#334155" }}>
            {formatDurationFromMs(reviewAge ?? stageAge)}
          </strong>
        </div>
        <button
          type="button"
          onClick={() => onOpenThread(ro)}
          style={{
            position: "relative",
            border: hasUnreadNote ? "1px solid #fdba74" : "1px solid rgba(15,23,42,0.12)",
            background: hasUnreadNote ? "#fff7ed" : "#ffffff",
            color: hasUnreadNote ? "#9a3412" : "#334155",
            borderRadius: 9,
            padding: "7px 9px",
            fontSize: 10.5,
            fontWeight: 900,
            cursor: "pointer",
            whiteSpace: "nowrap",
          }}
        >
          Thread
          {hasUnreadNote ? (
            <span
              style={{
                position: "absolute",
                top: -5,
                right: -5,
                width: 10,
                height: 10,
                borderRadius: 999,
                background: "#f97316",
                border: "2px solid #ffffff",
              }}
            />
          ) : null}
        </button>
      </div>
    </div>
  );
}

function PipelineRow({ label, count, total, detail, tone = "neutral" }) {
  const pct = total > 0 ? Math.max(0, Math.min(100, (count / total) * 100)) : 0;
  const color =
    tone === "danger"
      ? "#b42318"
      : tone === "warning"
      ? "#b54708"
      : tone === "good"
      ? "#067647"
      : tone === "parts"
      ? "#1d4ed8"
      : "#475569";

  return (
    <div style={{ padding: "8px 0", borderTop: "1px solid rgba(15,23,42,0.07)" }}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center" }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 11.6, fontWeight: 900, color: "#334155" }}>{label}</div>
          <div style={{ marginTop: 2, fontSize: 10.5, color: "rgba(15,23,42,0.52)" }}>{detail}</div>
        </div>
        <strong style={{ fontSize: 17, color }}>{count}</strong>
      </div>
      <div style={{ marginTop: 7, height: 5, borderRadius: 999, background: "#eef2f7", overflow: "hidden" }}>
        <div style={{ width: `${pct}%`, height: "100%", borderRadius: 999, background: color }} />
      </div>
    </div>
  );
}

function RankingList({ rows, emptyText }) {
  if (!rows.length) return <Empty>{emptyText}</Empty>;

  const max = Math.max(...rows.map((row) => row.count), 1);

  return (
    <div>
      {rows.map((row, index) => (
        <div
          key={row.name}
          style={{
            display: "grid",
            gridTemplateColumns: "24px minmax(0, 1fr) auto",
            gap: 8,
            alignItems: "center",
            padding: "7px 0",
            borderTop: "1px solid rgba(15,23,42,0.07)",
          }}
        >
          <span style={{ fontSize: 10.5, fontWeight: 900, color: "rgba(15,23,42,0.42)" }}>
            {index + 1}
          </span>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 11.5, fontWeight: 850, color: "#334155", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
              {row.name}
            </div>
            <div style={{ marginTop: 4, height: 4, borderRadius: 999, background: "#eef2f7", overflow: "hidden" }}>
              <div style={{ width: `${(row.count / max) * 100}%`, height: "100%", background: "#64748b" }} />
            </div>
          </div>
          <strong style={{ fontSize: 12, color: "#0f172a" }}>{row.count}</strong>
        </div>
      ))}
    </div>
  );
}

function ActivityRow({ entry, onOpenThread }) {
  const ro = entry?.ro || {};
  const actionAt = entry?.actionAt || getPartsActionISO(ro);
  const stage = entry?.stage || partsStage(ro);
  const request = requestTypeLabel(ro);

  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "minmax(170px, 1.1fr) minmax(140px, 0.8fr) minmax(160px, 1fr) auto",
        gap: 10,
        alignItems: "center",
        padding: "8px 0",
        borderTop: "1px solid rgba(15,23,42,0.07)",
      }}
    >
      <div style={{ minWidth: 0 }}>
        <strong style={{ fontSize: 11.7, color: "#0f172a" }}>{roNumberText(ro)}</strong>
        <div style={{ marginTop: 3, fontSize: 10.6, color: "rgba(15,23,42,0.56)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
          {[safeString(ro?.customerName).trim(), safeString(ro?.vehicle).trim()]
            .filter(Boolean)
            .join(" · ") || "—"}
        </div>
      </div>

      <div>
        <Badge tone={stage === "Arrived" ? "good" : stage === "Ordered" ? "warning" : stage === "Quoted" ? "parts" : "neutral"}>
          {stage}
        </Badge>
        {request ? (
          <div style={{ marginTop: 4, fontSize: 10.2, color: "rgba(15,23,42,0.48)" }}>{request}</div>
        ) : null}
      </div>

      <div style={{ fontSize: 10.7, color: "rgba(15,23,42,0.58)" }}>
        <div>Advisor: <b style={{ color: "#334155" }}>{safeString(ro?.advisorName).trim() || "—"}</b></div>
        <div style={{ marginTop: 3 }}>Tech: <b style={{ color: "#334155" }}>{safeString(ro?.assignedTechName).trim() || "—"}</b></div>
      </div>

      <div style={{ display: "flex", gap: 9, alignItems: "center", justifyContent: "flex-end" }}>
        <span style={{ fontSize: 10.5, color: "rgba(15,23,42,0.52)", whiteSpace: "nowrap" }}>
          {compactDateTime(actionAt)}
        </span>
        <button
          type="button"
          onClick={() => onOpenThread(ro)}
          style={{
            border: "1px solid rgba(15,23,42,0.12)",
            background: "#fff",
            color: "#334155",
            borderRadius: 8,
            padding: "6px 8px",
            fontSize: 10.3,
            fontWeight: 900,
            cursor: "pointer",
          }}
        >
          Thread
        </button>
      </div>
    </div>
  );
}

export default function PartsManagerDashboard() {
  const store = useROStore();
  const auth = useAuth();
  const {
    hasUnread,
    isHydrated: unreadHydrated,
    storeId: unreadStoreId,
  } = useROUnread();

  const all = normalizeROArray(store);
  const currentUser = store?.currentUser || null;

  const initialRange = presetDateRange(DATE_FILTERS.THIS_WEEK);

  const [dateFilter, setDateFilter] = useState(DATE_FILTERS.THIS_WEEK);
  const [from, setFrom] = useState(initialRange.from);
  const [to, setTo] = useState(initialRange.to);
  const [search, setSearch] = useState("");
  const [threadROId, setThreadROId] = useState("");
  const currentUnreadStoreId = safeString(
    auth?.effectiveStoreId || auth?.activeStoreId
  ).trim();
  const unreadStoreMatches =
    !!currentUnreadStoreId &&
    safeString(unreadStoreId).trim() === currentUnreadStoreId;

  const activeWip = useMemo(() => getActiveWip(all, store), [all, store]);

  const liveParts = useMemo(
    () => activeWip.filter((ro) => isPartsRelevant(ro)),
    [activeWip]
  );

  const liveReview = useMemo(
    () => liveParts.filter((ro) => hasOpenPartsReview(ro)),
    [liveParts]
  );

  const liveOrdered = useMemo(
    () => liveParts.filter((ro) => isOrdered(ro)),
    [liveParts]
  );

  const liveQuoteRequests = useMemo(
    () =>
      liveReview.filter(
        (ro) => getPartsRequestType(ro) === statusKey(PARTS_REQUEST_TYPE.NEED_PRICE)
      ),
    [liveReview]
  );

  const liveArrivedNotStarted = useMemo(
    () => liveParts.filter((ro) => isArrived(ro) && !isInProgress(ro)),
    [liveParts]
  );

  const livePromiseRisk = useMemo(
    () => liveParts.filter((ro) => isPromiseRisk(ro)),
    [liveParts]
  );

  const liveQuoted = useMemo(
    () => liveParts.filter((ro) => isQuoted(ro)),
    [liveParts]
  );

  const livePulled = useMemo(
    () => liveParts.filter((ro) => isPulled(ro)),
    [liveParts]
  );

  const reviewAges = useMemo(
    () =>
      liveReview
        .map((ro) => reviewAgeMs(ro))
        .filter((ms) => Number.isFinite(ms)),
    [liveReview]
  );

  const oldestReviewMs = reviewAges.length ? Math.max(...reviewAges) : 0;
  const averageReviewMs = reviewAges.length
    ? reviewAges.reduce((sum, ms) => sum + ms, 0) / reviewAges.length
    : 0;

  const dateRange = useMemo(() => {
    if (dateFilter === DATE_FILTERS.CUSTOM) {
      return {
        from,
        to,
        label: from && to ? `${from} to ${to}` : "Custom Range",
      };
    }
    return presetDateRange(dateFilter);
  }, [dateFilter, from, to]);

  const actionedInRange = useMemo(() => {
    return all
      .map((ro) => {
        const events = getPartsActionEvents(ro)
          .filter((event) =>
            inDateRangeISO(event.at, dateRange.from, dateRange.to)
          )
          .sort((a, b) => toTime(b.at) - toTime(a.at));

        const latest = events[0] || null;
        if (!latest) return null;

        return {
          ro,
          actionAt: latest.at,
          stage: stageFromActionEvent(latest, ro),
        };
      })
      .filter(Boolean)
      .sort((a, b) => toTime(b.actionAt) - toTime(a.actionAt));
  }, [all, dateRange]);

  const requestRowsInRange = useMemo(() => {
    const rows = [];

    all.forEach((ro) => {
      getPartsRequestEvents(ro).forEach((event) => {
        if (!inDateRangeISO(event.at, dateRange.from, dateRange.to)) return;
        rows.push({ ro, event });
      });
    });

    return rows;
  }, [all, dateRange]);

  const advisorSources = useMemo(
    () =>
      topCounts(requestRowsInRange, (row) => {
        if (row.event?.actorRole === "ADVISOR" && row.event?.actorName) {
          return row.event.actorName;
        }
        return row.ro?.advisorName;
      }),
    [requestRowsInRange]
  );

  const techSources = useMemo(
    () =>
      topCounts(requestRowsInRange, (row) => {
        if (
          ["TECH", "TECHNICIAN"].includes(row.event?.actorRole) &&
          row.event?.actorName
        ) {
          return row.event.actorName;
        }
        return row.ro?.assignedTechName;
      }),
    [requestRowsInRange]
  );

  const attentionList = useMemo(() => {
    const q = safeString(search).trim();

    return liveParts
      .filter((ro) => attentionReasons(ro).length > 0)
      .filter((ro) => searchMatches(ro, q))
      .sort((a, b) => attentionPriority(b) - attentionPriority(a))
      .slice(0, 18);
  }, [liveParts, search]);

  const threadRO = useMemo(
    () => findROById(all, threadROId),
    [all, threadROId]
  );

  const hasUnreadThreadNote = useCallback(
    (ro) => {
      const roId = safeString(ro?.id).trim();
      return !!(unreadHydrated && unreadStoreMatches && roId && hasUnread(roId));
    },
    [hasUnread, unreadHydrated, unreadStoreMatches]
  );

  const openThread = useCallback((ro) => {
    const roId = safeString(ro?.id).trim();
    if (!roId) return;

    setThreadROId(roId);
  }, []);

  const closeThread = useCallback(() => setThreadROId(""), []);

  function handleDateFilterChange(next) {
    setDateFilter(next);
    if (next !== DATE_FILTERS.CUSTOM) {
      const preset = presetDateRange(next);
      setFrom(preset.from);
      setTo(preset.to);
    }
  }

  const pipelineTotal = Math.max(liveParts.length, 1);
  const isLoading = !!store?.isLoading;

  return (
    <div
      style={{
        maxWidth: 1500,
        margin: "0 auto",
        padding: 12,
        display: "grid",
        gap: 10,
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 12,
          flexWrap: "wrap",
        }}
      >
        <div style={{ minWidth: 0 }}>
          <div
            style={{
              fontSize: 10.5,
              fontWeight: 900,
              letterSpacing: "0.12em",
              textTransform: "uppercase",
              color: "rgba(15,23,42,0.56)",
            }}
          >
            Parts Oversight
          </div>
          <h1
            style={{
              margin: "4px 0 0",
              fontSize: 24,
              lineHeight: 1.05,
              color: "#0f172a",
            }}
          >
            Parts Manager Dashboard
          </h1>
          <div
            style={{
              marginTop: 5,
              fontSize: 12,
              color: "rgba(15,23,42,0.60)",
            }}
          >
            Live workload, aging, promise risk, and parts throughput. The Parts Queue remains the action workspace.
          </div>
        </div>

        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <select
            value={dateFilter}
            onChange={(e) => handleDateFilterChange(e.target.value)}
            aria-label="Parts activity date range"
            style={{
              height: 36,
              borderRadius: 9,
              border: "1px solid rgba(15,23,42,0.13)",
              background: "#fff",
              padding: "0 10px",
              fontSize: 11.5,
              fontWeight: 800,
              color: "#334155",
            }}
          >
            <option value={DATE_FILTERS.TODAY}>Activity: Today</option>
            <option value={DATE_FILTERS.YESTERDAY}>Activity: Yesterday</option>
            <option value={DATE_FILTERS.THIS_WEEK}>Activity: This Week</option>
            <option value={DATE_FILTERS.THIS_MONTH}>Activity: This Month</option>
            <option value={DATE_FILTERS.CUSTOM}>Activity: Custom</option>
          </select>
          <Badge tone="parts">Live Parts ROs: {liveParts.length}</Badge>
        </div>
      </div>

      {dateFilter === DATE_FILTERS.CUSTOM ? (
        <div
          style={{
            display: "flex",
            gap: 8,
            alignItems: "center",
            flexWrap: "wrap",
            padding: "8px 10px",
            border: "1px solid rgba(15,23,42,0.08)",
            background: "#fff",
            borderRadius: 12,
          }}
        >
          <label style={{ display: "flex", gap: 6, alignItems: "center", fontSize: 10.8, fontWeight: 850, color: "#475569" }}>
            From
            <input
              type="date"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              style={{ height: 32, borderRadius: 8, border: "1px solid rgba(15,23,42,0.13)", padding: "0 8px" }}
            />
          </label>
          <label style={{ display: "flex", gap: 6, alignItems: "center", fontSize: 10.8, fontWeight: 850, color: "#475569" }}>
            To
            <input
              type="date"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              style={{ height: 32, borderRadius: 8, border: "1px solid rgba(15,23,42,0.13)", padding: "0 8px" }}
            />
          </label>
        </div>
      ) : null}

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
          gap: 8,
        }}
      >
        <MetricCard
          label="Open Parts Requests"
          value={isLoading ? "—" : liveReview.length}
          sub="Requests still waiting on Parts review."
          tone={liveReview.length ? "warning" : "good"}
        />
        <MetricCard
          label="Oldest Review"
          value={isLoading ? "—" : formatDurationFromMs(oldestReviewMs)}
          sub="Age since the oldest active parts request."
          tone={oldestReviewMs >= 24 * 60 * 60000 ? "danger" : oldestReviewMs ? "warning" : "good"}
        />
        <MetricCard
          label="Ordered, Not Arrived"
          value={isLoading ? "—" : liveOrdered.length}
          sub="Open ROs currently waiting on ordered parts."
          tone={liveOrdered.length ? "warning" : "neutral"}
        />
        <MetricCard
          label="Quote Requests"
          value={isLoading ? "—" : liveQuoteRequests.length}
          sub="Open price requests still waiting on Parts."
          tone={liveQuoteRequests.length ? "warning" : "good"}
        />
        <MetricCard
          label="Arrived, Work Not Started"
          value={isLoading ? "—" : liveArrivedNotStarted.length}
          sub="Parts are ready but repair work is not active."
          tone={liveArrivedNotStarted.length ? "parts" : "good"}
        />
        <MetricCard
          label="Average Review Age"
          value={isLoading ? "—" : formatDurationFromMs(averageReviewMs)}
          sub="Average wait since the parts request was sent."
          tone={averageReviewMs >= 8 * 60 * 60000 ? "warning" : "neutral"}
        />
        <MetricCard
          label="Actioned"
          value={isLoading ? "—" : actionedInRange.length}
          sub={`Unique ROs handled by Parts for ${dateRange.label}.`}
          tone="neutral"
        />
        <MetricCard
          label="Promise Risk Due to Parts"
          value={isLoading ? "—" : livePromiseRisk.length}
          sub="Parts-blocked ROs due within 60 minutes or overdue."
          tone={livePromiseRisk.length ? "danger" : "good"}
        />
      </div>

      <Section
        eyebrow="Current priorities"
        title="Attention Now"
        right={
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Find RO, tag, customer..."
            style={{
              width: 240,
              maxWidth: "42vw",
              height: 33,
              borderRadius: 9,
              border: "1px solid rgba(15,23,42,0.12)",
              padding: "0 9px",
              fontSize: 11,
            }}
          />
        }
      >
        {attentionList.length ? (
          <div style={{ overflowX: "auto", maxHeight: 460, overflowY: "auto" }}>
            <div style={{ minWidth: 760 }}>
              {attentionList.map((ro) => (
                <AttentionRow
                  key={ro.id}
                  ro={ro}
                  onOpenThread={openThread}
                  hasUnreadNote={hasUnreadThreadNote(ro)}
                />
              ))}
            </div>
          </div>
        ) : (
          <Empty>
            {search.trim()
              ? "No current parts priorities match your search."
              : "No active parts items require manager attention right now."}
          </Empty>
        )}
      </Section>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
          gap: 10,
          alignItems: "start",
        }}
      >
        <Section eyebrow="Live workload" title="Current Parts Pipeline">
          <PipelineRow
            label="Needs Review"
            count={liveReview.length}
            total={pipelineTotal}
            detail="New parts and quote requests awaiting action."
            tone={liveReview.length ? "warning" : "good"}
          />
          <PipelineRow
            label="Ordered"
            count={liveOrdered.length}
            total={pipelineTotal}
            detail="Waiting for ordered parts to arrive."
            tone="warning"
          />
          <PipelineRow
            label="Arrived"
            count={liveParts.filter((ro) => isArrived(ro)).length}
            total={pipelineTotal}
            detail="Parts have arrived for an open RO."
            tone="good"
          />
          <PipelineRow
            label="Quoted"
            count={liveQuoted.length}
            total={pipelineTotal}
            detail="Pricing supplied; customer or workflow follow-up may remain."
            tone="parts"
          />
          <PipelineRow
            label="Pre-Pulled / Available"
            count={livePulled.length}
            total={pipelineTotal}
            detail="Parts are available before repair work needs them."
            tone="good"
          />
        </Section>

        <Section
          eyebrow={`Requests · ${dateRange.label}`}
          title="Request Sources"
          right={<Badge>{requestRowsInRange.length} requests</Badge>}
        >
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
              gap: 12,
            }}
          >
            <div>
              <div style={{ fontSize: 10.2, fontWeight: 900, letterSpacing: "0.08em", textTransform: "uppercase", color: "rgba(15,23,42,0.50)", marginBottom: 4 }}>
                Advisors
              </div>
              <RankingList rows={advisorSources} emptyText="No advisor request activity in this range." />
            </div>
            <div>
              <div style={{ fontSize: 10.2, fontWeight: 900, letterSpacing: "0.08em", textTransform: "uppercase", color: "rgba(15,23,42,0.50)", marginBottom: 4 }}>
                Technicians
              </div>
              <RankingList rows={techSources} emptyText="No technician request activity in this range." />
            </div>
          </div>
        </Section>
      </div>

      <Section
        eyebrow={`Throughput · ${dateRange.label}`}
        title="Recent Parts Activity"
        right={<Badge tone="parts">{actionedInRange.length} actioned</Badge>}
      >
        {actionedInRange.length ? (
          <div style={{ overflowX: "auto", maxHeight: 390, overflowY: "auto" }}>
            <div style={{ minWidth: 700 }}>
              {actionedInRange.slice(0, 14).map((entry) => (
                <ActivityRow
                  key={entry.ro.id}
                  entry={entry}
                  onOpenThread={openThread}
                />
              ))}
            </div>
          </div>
        ) : (
          <Empty>No parts activity was recorded for {dateRange.label}.</Empty>
        )}
      </Section>

      <ROActivityDrawer
        ro={threadRO}
        isOpen={!!threadRO}
        onClose={closeThread}
        currentUser={currentUser}
        actorRole={safeString(currentUser?.role || "manager").trim() || "manager"}
        actorName={safeString(currentUser?.name || "Manager").trim() || "Manager"}
      />
    </div>
  );
}
